import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Flex,
  Skeleton,
  Text,
  VStack,
  HStack,
  Icon,
  Badge,
  Avatar,
  Divider,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  useDisclosure
} from '@chakra-ui/react';
import MapBoxField from '../../../../components/map/MapBoxField';
import { Card, CardHeader, CardTitle, Right, CardContent } from "../../../../components/Card";
import InfoBox from "../../../../components/InfoBox";
import client from '../../../../helpers/Client';
import urlToId from '../../../../helpers/UrlToId';
import { ArrowForwardIcon } from "@chakra-ui/icons";
import { FiUser, FiPhone, FiMapPin, FiMessageSquare, FiHome } from "react-icons/fi";
import { MdLocationCity, MdVerified } from "react-icons/md";
import Notes from "../../../../components/notes/Notes";
import { useNavigate } from "react-router-dom";
import getFullName from "../../../../helpers/FullName";
import PhoneNumber from "../../../../components/buttons/PhoneNumber";

// Text Detail Modal Component
function TextDetailModal({ isOpen, onClose, title, value, icon, color = "blue", showMap = false }) {
  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md" isCentered>
      <ModalOverlay bg="blackAlpha.300" backdropFilter="blur(10px)" />
      <ModalContent>
        <ModalHeader>
          <HStack spacing={3}>
            <Icon as={icon} boxSize={5} color={`${color}.500`} />
            <Text>{title}</Text>
          </HStack>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6}>
          <Text
            fontSize="md"
            lineHeight="1.6"
            color="gray.700"
            whiteSpace="pre-wrap"
            wordBreak="break-word"
          >
            {value || "Belirtilmemiş"}
          </Text>
          {showMap && (
            <Box mt={3}>
              <MapBoxField value={value} onChangeAddress={() => { }} height={240} />
            </Box>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}

// Modern Info Card Component with Long Press Support
function ModernInfoCard({ title, value, icon, color = "blue", isVerified, onClick, enableLongPress = false, showMapInModal = false }) {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [longPressTimer, setLongPressTimer] = useState(null);

  const cardBg = "rgba(255, 255, 255, 0.8)";
  const borderColor = "gray.200";
  const textColor = "gray.600";

  const handleMouseDown = () => {
    if (enableLongPress && value && value.length > 30) {
      const timer = setTimeout(() => {
        onOpen();
      }, 800); // 800ms long press
      setLongPressTimer(timer);
    }
  };

  const handleMouseUp = () => {
    if (longPressTimer) {
      clearTimeout(longPressTimer);
      setLongPressTimer(null);
    }
  };

  const handleMouseLeave = () => {
    if (longPressTimer) {
      clearTimeout(longPressTimer);
      setLongPressTimer(null);
    }
  };

  const handleTouchStart = () => {
    if (enableLongPress && value && value.length > 30) {
      const timer = setTimeout(() => {
        onOpen();
      }, 800);
      setLongPressTimer(timer);
    }
  };

  const handleTouchEnd = () => {
    if (longPressTimer) {
      clearTimeout(longPressTimer);
      setLongPressTimer(null);
    }
  };

  const shouldShowLongPressHint = enableLongPress && value && value.length > 30;

  return (
    <>
      <Box
        bg={cardBg}
        backdropFilter="blur(20px)"
        border="1px solid"
        borderColor={borderColor}
        borderRadius="lg"
        p={3}
        transition="all 0.3s ease"
        _hover={{
          transform: "translateY(-1px)",
          boxShadow: "md",
          borderColor: `${color}.300`,
        }}
        cursor={onClick ? "pointer" : shouldShowLongPressHint ? "pointer" : "default"}
        onClick={onClick}
        onMouseDown={handleMouseDown}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseLeave}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
        w="full"
        position="relative"
      >
        <HStack spacing={3} align="center">
          <Icon as={icon} boxSize={4} color={`${color}.500`} flexShrink={0} />
          <VStack align="start" spacing={0} flex={1} minW={0}>
            <Text fontSize="xs" color={textColor} fontWeight="medium">
              {title}
              {shouldShowLongPressHint && (
                <Text as="span" fontSize="xs" color="gray.400" ml={1}>
                  (uzun basın)
                </Text>
              )}
            </Text>
            <Text
              fontSize="sm"
              fontWeight="semibold"
              color={`${color}.600`}
              noOfLines={1}
              wordBreak="break-all"
            >
              {value || "Belirtilmemiş"}
            </Text>
          </VStack>
          {isVerified !== undefined && (
            <Badge
              colorScheme={isVerified ? "green" : "red"}
              variant="subtle"
              borderRadius="full"
              px={2}
              py={1}
              fontSize="xs"
              flexShrink={0}
            >
              <Icon
                as={MdVerified}
                boxSize={2}
                mr={1}
              />
              {isVerified ? "✓" : "✗"}
            </Badge>
          )}
        </HStack>
      </Box>

      {enableLongPress && (
        <TextDetailModal
          isOpen={isOpen}
          onClose={onClose}
          title={title}
          value={value}
          icon={icon}
          color={color}
          showMap={showMapInModal}
        />
      )}
    </>
  );
}

function CustomerInfo({ customer }) {
  const cardBg = "rgba(255, 255, 255, 0.9)";
  const borderColor = "gray.200";

  return (
    <VStack spacing={6} align="stretch">
      {/* Header with Avatar */}
      <VStack spacing={4} align="center">
        <Avatar
          size="lg"
          name={getFullName(customer.user)}
          bg="blue.500"
          color="white"
          fontSize="lg"
        />
        <VStack spacing={1} textAlign="center">
          <Text fontSize="md" fontWeight="bold" color="blue.600">
            {getFullName(customer.user)}
          </Text>
          <Text fontSize="xs" color="gray.500">
            Müşteri ID: #{urlToId(customer.url)}
          </Text>
        </VStack>
      </VStack>

      {/* Info Cards Grid */}
      <VStack spacing={3} align="stretch">
        <ModernInfoCard
          title="Telefon"
          value={customer.phone_number}
          icon={FiPhone}
          color="green"
          isVerified={customer.phone_verified}
        />
        <ModernInfoCard
          title="Şehir"
          value={customer.city_name}
          icon={MdLocationCity}
          color="purple"
        />
        <ModernInfoCard
          title="Adres"
          value={customer.address}
          icon={FiMapPin}
          color="orange"
          enableLongPress={true}
          showMapInModal={true}
        />
      </VStack>
    </VStack>
  );
}

export default function Customer({ customerID, ticket, onLoadingChange }) {
  const [customer, setCustomer] = useState();
  const navigate = useNavigate();

  const cardBg = "rgba(236, 236, 236, 0.68)";
  const borderColor = "gray.200";
  const headerBg = "linear(to-r, red.500, red.600)";

  const handleCustomer = () => {
    navigate(`/musteriler/${urlToId(customer.url)}`);
  };

  // Get customerID from ticket if not provided directly
  const actualCustomerID = customerID || (ticket?.customer ? urlToId(ticket.customer) : null);

  useEffect(() => {
    if (!actualCustomerID) {
      console.warn('Customer component: customerID is undefined');
      return;
    }
    onLoadingChange && onLoadingChange(true);
    client.get('/customers/' + actualCustomerID + '/').then(
      (response) => {
        setCustomer(response.data);
        onLoadingChange && onLoadingChange(false);
      }).catch((error) => {
        console.error('Error fetching customer:', error);
        onLoadingChange && onLoadingChange(false);
      });
  }, [actualCustomerID, onLoadingChange]);

  return (
    <Box
      bg={cardBg}
      backdropFilter="blur(20px)"
      border="1px solid"
      borderColor={borderColor}
      borderRadius="2xl"
      overflow="hidden"
      shadow="xl"
    >
      {/* Header */}
      <Box
        bgGradient={headerBg}
        px={6}
        py={4}
      >
        <HStack justify="space-between" align="center">
          <HStack spacing={3}>
            <Icon as={FiUser} color="white" boxSize={5} />
            <Text color="white" fontSize="lg" fontWeight="bold">
              {customer ? getFullName(customer.user) : "Müşteri Bilgileri"}
            </Text>
          </HStack>
          {customer && (
            <Button
              onClick={handleCustomer}
              variant="Ghost"
              size='sm'
              colorScheme="whiteAlpha"
            >
              Müşteriyi Görüntüle
            </Button>
          )}
        </HStack>
      </Box>

      {/* Content */}
      <Box p={6}>
        <Skeleton isLoaded={customer}>
          {customer ? (
            <Tabs variant="enclosed" colorScheme="blue" size="sm">
              <TabList>
                <Tab fontSize="sm">
                  <Icon as={FiUser} mr={1} boxSize={3} />
                  Bilgiler
                </Tab>
                <Tab fontSize="sm">
                  <Icon as={FiMessageSquare} mr={1} boxSize={3} />
                  Notlar
                </Tab>
              </TabList>

              <TabPanels>
                <TabPanel p={4}>
                  <CustomerInfo customer={customer} />
                  <Divider my={3} />
                  <Text fontSize="xs" color="gray.500" textAlign="center">
                    Sistem üzerinde kayıtlı olan müşteri bilgileri, iş emrindeki bilgiler ile değişiklik gösterebilir.
                  </Text>
                </TabPanel>
                <TabPanel p={2}>
                  <Box maxH="400px" overflowY="auto">
                    <Notes
                      object_model="customer.customer"
                      object_id={urlToId(customer.url)}
                      isNaive={true}
                    />
                  </Box>
                </TabPanel>
              </TabPanels>
            </Tabs>
          ) : (
            <VStack spacing={4}>
              <HStack spacing={3} w="full">
                <Skeleton height="60px" width="60px" borderRadius="full" />
                <VStack align="start" spacing={2} flex={1}>
                  <Skeleton height="20px" width="60%" />
                  <Skeleton height="16px" width="40%" />
                </VStack>
              </HStack>
              <HStack spacing={4} w="full">
                <Skeleton height="80px" flex={1} borderRadius="xl" />
                <Skeleton height="80px" flex={1} borderRadius="xl" />
                <Skeleton height="80px" flex={1} borderRadius="xl" />
              </HStack>
            </VStack>
          )}
        </Skeleton>
      </Box>
    </Box>
  );
}
